import React from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@material-ui/core';
import { Button } from 'm2m.components';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import WarningIcon from '@material-ui/icons/Warning';

export type FeedbackLoteStatus = 'SUCESSO' | 'PARCIAL' | 'FALHA';

export interface FeedbackLoteResumo {
  totalProcessados: number;
  totalSucesso: number;
  totalFalha: number;
}

export interface FeedbackLoteErroItem {
  identificador: string;
  campos?: Array<{ label: string; valor: string }>;
  mensagemErro: string;
}

export interface FeedbackLoteData {
  status: FeedbackLoteStatus;
  titulo?: string;
  resumo?: FeedbackLoteResumo;
  erros: FeedbackLoteErroItem[];
  errosSecundarios?: {
    titulo: string;
    descricao?: string;
    itens: FeedbackLoteErroItem[];
  };
}

interface FeedbackLoteModalProps {
  open: boolean;
  onClose: () => void;
  data: FeedbackLoteData;
  tituloFalha?: string;
}

const statusConfig: Record<FeedbackLoteStatus, { icon: React.ReactNode; color: string; tituloDefault: string }> = {
  SUCESSO: {
    icon: <CheckCircleOutlineIcon style={{ fontSize: 40, color: '#4caf50' }} />,
    color: '#4caf50',
    tituloDefault: 'Processado com sucesso',
  },
  PARCIAL: {
    icon: <WarningIcon style={{ fontSize: 40, color: '#ff9800' }} />,
    color: '#ff9800',
    tituloDefault: 'Processado parcialmente',
  },
  FALHA: {
    icon: <ErrorOutlineIcon style={{ fontSize: 40, color: '#f44336' }} />,
    color: '#f44336',
    tituloDefault: 'Processamento falhou',
  },
};

const FeedbackLoteModal: React.FC<FeedbackLoteModalProps> = ({ open, onClose, data, tituloFalha }) => {
  const config = statusConfig[data.status];
  const titulo = data.titulo ?? config.tituloDefault;
  const hasErrors = data.erros.length > 0;
  const hasSecondaryErrors = data.errosSecundarios && data.errosSecundarios.itens.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={hasErrors || hasSecondaryErrors ? 'md' : 'sm'} fullWidth>
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
          {config.icon}
          <Typography variant="h6" style={{ color: config.color }}>
            {titulo}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {data.resumo && (
          <Box display="flex" style={{ gap: '24px', marginBottom: '20px' }}>
            {[
              { label: 'Total', value: data.resumo.totalProcessados },
              { label: 'Sucesso', value: data.resumo.totalSucesso, color: '#4caf50' },
              { label: 'Falha', value: data.resumo.totalFalha, color: '#f44336' },
            ].map((card) => (
              <Box
                key={card.label}
                p={1.5}
                style={{ border: '1px solid #e0e0e0', borderRadius: '4px', minWidth: '80px', textAlign: 'center' }}
              >
                <Typography variant="caption" color="textSecondary">
                  {card.label}
                </Typography>
                <Typography variant="h5" style={{ color: card.color }}>
                  <strong>{card.value}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {hasErrors && (
          <>
            {data.resumo && <Divider style={{ marginBottom: '16px' }} />}
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{tituloFalha ?? 'Itens com falha'}</strong>
            </Typography>
            {data.erros.map((item, index) => (
              <Box
                key={`${item.identificador}-${index}`}
                mb={1}
                p={1.5}
                style={{ border: '1px solid #ffcdd2', borderRadius: '4px', backgroundColor: '#fff5f5' }}
              >
                {item.campos && item.campos.length > 0 ? (
                  <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                    {item.campos.map((campo) => (
                      <Box key={campo.label}>
                        <Typography variant="caption" color="textSecondary">
                          {campo.label}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{campo.valor}</strong>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {item.identificador}
                  </Typography>
                )}
                <Box mt={1}>
                  <Typography variant="body2" color="error">
                    {item.mensagemErro}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}

        {hasSecondaryErrors && data.errosSecundarios && (
          <>
            <Box mt={3}>
              <Divider style={{ marginBottom: '16px' }} />
            </Box>
            <Typography variant="subtitle1" style={{ marginBottom: '12px' }}>
              <strong>{data.errosSecundarios.titulo}</strong>
            </Typography>
            {data.errosSecundarios.descricao && (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                {data.errosSecundarios.descricao}
              </Typography>
            )}
            {data.errosSecundarios.itens.map((item, index) => (
              <Box
                key={`${item.identificador}-${index}`}
                mb={1}
                p={1.5}
                style={{ border: '1px solid #ffe0b2', borderRadius: '4px', backgroundColor: '#fff8e1' }}
              >
                {item.campos && item.campos.length > 0 ? (
                  <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                    {item.campos.map((campo) => (
                      <Box key={campo.label}>
                        <Typography variant="caption" color="textSecondary">
                          {campo.label}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{campo.valor}</strong>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {item.identificador}
                  </Typography>
                )}
                <Box mt={1}>
                  <Typography variant="body2" color="error">
                    {item.mensagemErro}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button variant="contained" color="primary" onClick={onClose}>
          {'Fechar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackLoteModal;
